import { useState } from "react";
import { mascararCelular } from "./formato";
import { interessadoSchema, InteressadoSchema } from "./schema";

export type DadosInteressado = InteressadoSchema;

type Props = {
  /** Lead já registrado — o formulário some e dá lugar ao resumo. */
  registrado: DadosInteressado | null;
  enviando: boolean;
  onRegistrar: (dados: DadosInteressado) => void;
  onEditar: () => void;
};

type Erros = Partial<Record<keyof DadosInteressado, string>>;

/**
 * Primeiro passo da página. O contato é gravado antes de o interessado escolher
 * qualquer coisa: é o que garante o lead mesmo quando ele abandona o orçamento
 * no meio. Enquanto não for enviado, nada abaixo aparece.
 */
export function GateInteressado({ registrado, enviando, onRegistrar, onEditar }: Props) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [celular, setCelular] = useState("");
  const [erros, setErros] = useState<Erros>({});

  if (registrado) {
    return (
      <div
        className="op-panel"
        style={{ background: "var(--op-indigo-wash)", borderColor: "rgba(38,47,110,.3)" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "13px 16px",
            flexWrap: "wrap",
          }}
        >
          <span aria-hidden="true" style={{ color: "var(--op-indigo)", fontWeight: 800 }}>
            ✓
          </span>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 15 }}>{registrado.nome}</b>
            <span
              style={{ fontSize: 13.5, color: "var(--op-muted)", overflowWrap: "anywhere" }}
            >
              {registrado.email} · {registrado.celular}
            </span>
          </div>
          <button
            type="button"
            onClick={onEditar}
            style={{
              background: "none",
              border: "none",
              padding: "4px 2px",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 13.5,
              color: "var(--op-indigo)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Alterar
          </button>
        </div>
      </div>
    );
  }

  const submeter = (evento: React.FormEvent) => {
    evento.preventDefault();

    const resultado = interessadoSchema.safeParse({ nome, email, celular });
    if (!resultado.success) {
      const novosErros: Erros = {};
      resultado.error.issues.forEach((issue) => {
        const campo = issue.path[0] as keyof DadosInteressado;
        if (campo && !novosErros[campo]) novosErros[campo] = issue.message;
      });
      setErros(novosErros);
      return;
    }

    setErros({});
    onRegistrar(resultado.data);
  };

  return (
    <form className="op-panel" onSubmit={submeter} noValidate>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
          padding: 18,
        }}
      >
        <Campo
          id="op-nome"
          rotulo="Seu nome completo"
          erro={erros.nome}
          largura="total"
          valor={nome}
          onChange={setNome}
          placeholder="Ana Paula Ribeiro de Souza"
          autoComplete="name"
        />
        <Campo
          id="op-email"
          rotulo="E-mail"
          erro={erros.email}
          valor={email}
          onChange={setEmail}
          placeholder="ana.souza@email.com"
          type="email"
          autoComplete="email"
        />
        <Campo
          id="op-celular"
          rotulo="Celular"
          erro={erros.celular}
          valor={celular}
          onChange={(valor) => setCelular(mascararCelular(valor))}
          placeholder="(61) 99999-0000"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          maxLength={15}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "center",
          padding: 18,
          borderTop: "1px solid var(--op-line)",
        }}
      >
        <button type="submit" className="op-btn op-btn-primary" disabled={enviando}>
          {enviando ? "Salvando..." : "Seguir para Orçamento"}
        </button>
        <span style={{ fontSize: 13, color: "var(--op-faint)", flex: 1, minWidth: 200 }}>
          Usamos seu contato só para falar sobre este orçamento. Você monta a proposta na
          sequência.
        </span>
      </div>
    </form>
  );
}

type CampoProps = {
  id: string;
  rotulo: string;
  valor: string;
  onChange: (valor: string) => void;
  erro?: string;
  largura?: "total";
  placeholder?: string;
  type?: string;
  inputMode?: "numeric";
  autoComplete?: string;
  maxLength?: number;
};

function Campo({ id, rotulo, valor, onChange, erro, largura, ...resto }: CampoProps) {
  return (
    <div style={largura === "total" ? { gridColumn: "1 / -1" } : undefined}>
      <label
        htmlFor={id}
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: "var(--op-muted)",
          marginBottom: 5,
        }}
      >
        {rotulo}
      </label>
      <input
        id={id}
        className="op-inp"
        value={valor}
        onChange={(evento) => onChange(evento.target.value)}
        aria-invalid={erro ? "true" : undefined}
        aria-describedby={erro ? `${id}-erro` : undefined}
        {...resto}
      />
      {erro && (
        <div id={`${id}-erro`} style={{ color: "var(--op-crimson)", fontSize: 13, marginTop: 4 }}>
          {erro}
        </div>
      )}
    </div>
  );
}

export default GateInteressado;
